/**
 * VITAE-PUBLICA™ — Chart utilities.
 * Central palette, formatters, pedagogical helpers.
 */

export const PALETTE = {
  blue: "#1A3C5E",
  gold: "#C9A84C",
  red: "#C0392B",
  green: "#3B7A57",
  slate: "#3E6B7D",
  amber: "#B8812A",
  muted: "#94A3B8",
  stateTax: "#C0392B", // days consumed by the state
  freedom: "#2F7A3B", // days for self
};

// Consistent sequence for up to 8 series
export const SERIES_COLORS = [
  PALETTE.blue,
  PALETTE.gold,
  PALETTE.red,
  PALETTE.slate,
  PALETTE.amber,
  PALETTE.green,
  "#6B4C9A",
  "#8C6239",
];

export function fmtNumber(v, digits = 0) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return Number(v).toLocaleString("fr-FR", {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
}

export function fmtMoney(v, currency = "UM") {
  if (v === null || v === undefined) return "—";
  return `${fmtNumber(v, 0)} ${currency}`;
}

export function fmtDays(v) {
  if (v === null || v === undefined) return "—";
  return `${fmtNumber(v, 1)} jours`;
}

export function fmtMonths(v) {
  if (v === null || v === undefined) return "—";
  return `${fmtNumber(v, 2)} mois`;
}

export function fmtYears(v) {
  if (v === null || v === undefined) return "—";
  return `${fmtNumber(v, 2)} ans`;
}

export function fmtPct(v, digits = 2) {
  if (v === null || v === undefined) return "—";
  return `${fmtNumber(v, digits)} %`;
}

/**
 * Tax Freedom Day — date symbolique à partir du 1er janvier,
 * correspondant au jour à partir duquel le contribuable "travaille pour lui-même".
 *
 * Convention vitaliste (EQ-V3) :
 *   1er janvier = jour 1 du calendrier fiscal vitaliste.
 *   Le contribuable a sacrifié `J_vie` jours pour l'État → le jour de
 *   libération est le `J_vie`-ième jour de l'année (et non le `J_vie+1`-ième).
 *   Exemple : J_vie = 108 → 18 avril (108e jour, hors année bissextile).
 *
 * Input : days_for_state (jours_vie_sacrifies_par_an)
 * Output : { label: "18 avril", isoDate: "YYYY-04-18", day_number: 108 }
 */
export function taxFreedomDay(days_for_state, year = new Date().getFullYear()) {
  if (days_for_state === null || days_for_state === undefined) return null;
  const raw = Math.round(Number(days_for_state));
  // EQ-V3 : autoriser dépassement d'année (J_vie > 365) pour profils sur-prélevés.
  // Le label restera tronqué à la dernière année représentable (cas EQ-V3 test B).
  const d = Math.max(1, raw); // au moins 1 jour
  const date = new Date(year, 0, 1);
  date.setDate(date.getDate() + (d - 1)); // jour J = base (1er jan) + (d-1) jours
  const label = date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: date.getFullYear() !== year ? "numeric" : undefined,
  });
  return {
    label,
    isoDate: date.toISOString().slice(0, 10),
    day_number: d,
  };
}

/**
 * Convert a monetary amount into "days of life" using the reference daily wage.
 */
export function daysFromMoney(amount, salaire_journalier) {
  if (!amount || !salaire_journalier || salaire_journalier <= 0) return null;
  return amount / salaire_journalier;
}
